import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { Check, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

// Form validation schema
const formSchema = z.object({
  firstName: z.string().min(1, "First name is required").max(50, "First name is too long"),
  lastName: z.string().min(1, "Last name is required").max(50, "Last name is too long"),
  email: z.string().email("Please enter a valid email address"),
  phone: z
    .string()
    .min(10, "Phone number must be at least 10 digits")
    .regex(/^[\d\s\-\(\)\+]+$/, "Please enter a valid phone number"),
  preferredService: z.string().min(1, "Please select a service"),
  preferredTime: z.string().optional(),
  message: z.string().max(500, "Message is too long").optional(),
  marketingConsent: z.boolean().default(false),
  nonMarketingConsent: z.boolean().default(false),
});

const SERVICES = [
  { value: "physiotherapy", label: "Physiotherapy" },
  { value: "chiropractic", label: "Chiropractic" },
  { value: "massage-therapy", label: "Registered Massage Therapy" },
  { value: "naturopathic", label: "Naturopathic Care" },
  { value: "fitness", label: "Fitness Programs" },
  { value: "other", label: "Other / General Inquiry" },
];

const TIME_SLOTS = [
  { value: "morning", label: "Morning (9AM - 12PM)" },
  { value: "afternoon", label: "Afternoon (12PM - 4PM)" },
  { value: "evening", label: "Evening (4PM - 7PM)" },
  { value: "flexible", label: "Flexible / Any Time" },
];

export default function SMSConsentForm() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
    reset,
  } = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      preferredService: "",
      preferredTime: "",
      message: "",
      marketingConsent: false,
      nonMarketingConsent: false,
    },
  });

  const marketingConsent = watch("marketingConsent");
  const nonMarketingConsent = watch("nonMarketingConsent");
  const preferredService = watch("preferredService");
  const preferredTime = watch("preferredTime");

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    
    // Simulate form submission delay
    await new Promise((resolve) => setTimeout(resolve, 1500));
    
    console.log("Form submitted:", data);
    
    setIsSubmitting(false);
    setIsSubmitted(true);
    
    toast.success("Request Submitted Successfully!", {
      description: "We'll be in touch shortly to confirm your appointment.",
    });
  };

  if (isSubmitted) {
    return (
      <div 
        className="form-card p-8 md:p-10 text-center"
        data-testid="form-success-message"
      >
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6 animate-checkmark">
          <Check className="h-8 w-8 text-green-600" />
        </div>
        <h3 className="font-heading text-2xl font-semibold text-slate-800 mb-3">
          Thank You!
        </h3>
        <p className="text-slate-600 mb-6">
          Your appointment request has been submitted successfully. 
          Our team will contact you shortly to confirm your booking.
        </p>
        <Button
          onClick={() => {
            setIsSubmitted(false);
            reset();
          }}
          variant="outline"
          className="rounded-full"
          data-testid="submit-another-btn"
        >
          Submit Another Request
        </Button>
      </div>
    );
  }

  return (
    <form 
      onSubmit={handleSubmit(onSubmit)} 
      className="form-card p-6 md:p-8 space-y-6"
      data-testid="sms-consent-form"
    >
      <div className="text-center mb-6">
        <h2 className="font-heading text-2xl font-semibold text-slate-800 mb-2">
          Request an Appointment
        </h2>
        <p className="text-slate-500 text-sm">
          Fill out the form below and our team will follow up regarding your requested service and appointment preferences.
        </p>
      </div>

      {/* Name Fields */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="firstName" className="text-slate-700">
            First Name <span className="text-red-500">*</span>
          </Label>
          <Input
            id="firstName"
            {...register("firstName")}
            placeholder="John"
            className="h-12 bg-white border-slate-200 focus:border-[#0047AB] focus:ring-[#0047AB]/20"
            data-testid="input-first-name"
          />
          {errors.firstName && (
            <p className="text-red-500 text-sm" data-testid="error-first-name">
              {errors.firstName.message}
            </p>
          )}
        </div>
        <div className="space-y-2">
          <Label htmlFor="lastName" className="text-slate-700">
            Last Name <span className="text-red-500">*</span>
          </Label>
          <Input
            id="lastName"
            {...register("lastName")}
            placeholder="Doe"
            className="h-12 bg-white border-slate-200 focus:border-[#0047AB] focus:ring-[#0047AB]/20"
            data-testid="input-last-name"
          />
          {errors.lastName && (
            <p className="text-red-500 text-sm" data-testid="error-last-name">
              {errors.lastName.message}
            </p>
          )}
        </div>
      </div>

      {/* Contact Fields */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="email" className="text-slate-700">
            Email <span className="text-red-500">*</span>
          </Label>
          <Input
            id="email"
            type="email"
            {...register("email")}
            placeholder="john@example.com"
            className="h-12 bg-white border-slate-200 focus:border-[#0047AB] focus:ring-[#0047AB]/20"
            data-testid="input-email"
          />
          {errors.email && (
            <p className="text-red-500 text-sm" data-testid="error-email">
              {errors.email.message}
            </p>
          )}
        </div>
        <div className="space-y-2">
          <Label htmlFor="phone" className="text-slate-700">
            Phone Number <span className="text-red-500">*</span>
          </Label>
          <Input
            id="phone"
            type="tel"
            {...register("phone")}
            placeholder="(416) 555-0123"
            className="h-12 bg-white border-slate-200 focus:border-[#0047AB] focus:ring-[#0047AB]/20"
            data-testid="input-phone"
          />
          {errors.phone && (
            <p className="text-red-500 text-sm" data-testid="error-phone">
              {errors.phone.message}
            </p>
          )}
        </div>
      </div>

      {/* Service and Time */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="preferredService" className="text-slate-700">
            Preferred Service <span className="text-red-500">*</span>
          </Label>
          <Select 
            value={preferredService} 
            onValueChange={(value) => setValue("preferredService", value)}
          >
            <SelectTrigger 
              className="h-12 bg-white border-slate-200 focus:border-[#0047AB] focus:ring-[#0047AB]/20"
              data-testid="select-service"
            >
              <SelectValue placeholder="Select a service" />
            </SelectTrigger>
            <SelectContent>
              {SERVICES.map((service) => (
                <SelectItem 
                  key={service.value} 
                  value={service.value}
                  data-testid={`service-option-${service.value}`}
                >
                  {service.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.preferredService && (
            <p className="text-red-500 text-sm" data-testid="error-service">
              {errors.preferredService.message}
            </p>
          )}
        </div>
        <div className="space-y-2">
          <Label htmlFor="preferredTime" className="text-slate-700">
            Preferred Time
          </Label>
          <Select 
            value={preferredTime} 
            onValueChange={(value) => setValue("preferredTime", value)}
          >
            <SelectTrigger 
              className="h-12 bg-white border-slate-200 focus:border-[#0047AB] focus:ring-[#0047AB]/20"
              data-testid="select-time"
            >
              <SelectValue placeholder="Select preferred time" />
            </SelectTrigger>
            <SelectContent>
              {TIME_SLOTS.map((slot) => (
                <SelectItem 
                  key={slot.value} 
                  value={slot.value}
                  data-testid={`time-option-${slot.value}`}
                >
                  {slot.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Message */}
      <div className="space-y-2">
        <Label htmlFor="message" className="text-slate-700">
          Message / Reason for Visit
        </Label>
        <Textarea
          id="message"
          {...register("message")}
          placeholder="Please describe your symptoms, concerns, or what you'd like help with..."
          className="min-h-[100px] bg-white border-slate-200 focus:border-[#0047AB] focus:ring-[#0047AB]/20 resize-none"
          data-testid="input-message"
        />
        {errors.message && (
          <p className="text-red-500 text-sm" data-testid="error-message">
            {errors.message.message}
          </p>
        )}
      </div>

      {/* SMS Consent Section */}
      <div className="space-y-4 pt-4 border-t border-slate-100">
        <h3 className="font-heading font-semibold text-slate-800">
          SMS Communication Preferences (Optional)
        </h3>
        
        {/* Marketing Consent Checkbox */}
        <div 
          className={`consent-box ${marketingConsent ? 'checked' : ''}`}
          data-testid="marketing-consent-container"
        >
          <div className="flex items-start space-x-3">
            <Checkbox
              id="marketingConsent"
              checked={marketingConsent}
              onCheckedChange={(checked) => setValue("marketingConsent", checked)}
              className="mt-1 h-5 w-5 border-slate-300 data-[state=checked]:bg-[#0047AB] data-[state=checked]:border-[#0047AB]"
              data-testid="checkbox-marketing-consent"
            />
            <Label 
              htmlFor="marketingConsent" 
              className="text-sm text-slate-600 leading-relaxed cursor-pointer font-normal"
            >
              I consent to receive marketing text messages, about special offers, discounts, and 
              service updates, from Muscle & Movement Rehab at the phone number provided. Message 
              frequency may vary. Message & data rates may apply. Text HELP for assistance, reply 
              STOP to opt out.
            </Label>
          </div>
        </div>

        {/* Non-Marketing Consent Checkbox */}
        <div 
          className={`consent-box ${nonMarketingConsent ? 'checked' : ''}`}
          data-testid="non-marketing-consent-container"
        >
          <div className="flex items-start space-x-3">
            <Checkbox
              id="nonMarketingConsent"
              checked={nonMarketingConsent}
              onCheckedChange={(checked) => setValue("nonMarketingConsent", checked)}
              className="mt-1 h-5 w-5 border-slate-300 data-[state=checked]:bg-[#0047AB] data-[state=checked]:border-[#0047AB]"
              data-testid="checkbox-non-marketing-consent"
            />
            <Label 
              htmlFor="nonMarketingConsent" 
              className="text-sm text-slate-600 leading-relaxed cursor-pointer font-normal"
            >
              I consent to receive non-marketing text messages from Muscle & Movement Rehab about 
              appointment reminders, booking confirmations, follow-up messages, and service-related 
              notifications. Message frequency may vary, message & data rates may apply. Text HELP 
              for assistance, reply STOP to opt out.
            </Label>
          </div>
        </div>
      </div>

      {/* Legal Links */}
      <div className="text-sm text-slate-500 pt-2">
        <p>
          By submitting this form, you agree to our{" "}
          <a 
            href="/privacy-policy" 
            target="_blank"
            className="text-[#0047AB] hover:underline"
            data-testid="form-privacy-link"
          >
            Privacy Policy
          </a>{" "}
          and{" "}
          <a 
            href="/terms-conditions" 
            target="_blank"
            className="text-[#0047AB] hover:underline"
            data-testid="form-terms-link"
          >
            Terms & Conditions
          </a>.
        </p>
      </div>

      {/* Submit Button */}
      <Button
        type="submit"
        disabled={isSubmitting}
        className="w-full h-12 bg-[#0047AB] hover:bg-[#003580] text-white font-semibold rounded-full shadow-lg transition-all btn-primary"
        data-testid="submit-btn"
      >
        {isSubmitting ? (
          <>
            <Loader2 className="h-5 w-5 animate-spin mr-2" />
            Submitting...
          </>
        ) : (
          "Request Appointment"
        )}
      </Button>
    </form>
  );
}
